import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useState, useEffect } from "react";
import API from "@/lib/api";
import { LayoutDashboard, Users, Package, QrCode, Settings, LogOut, BarChart3, Image, Video, BookOpen, Newspaper, MessageSquare, Headphones, UserPlus, FlaskConical, Gift, MapPin, Sparkles, Layers, Microscope, ShieldCheck, FileUp, ShieldAlert, Stethoscope, ScrollText, ScanLine } from "lucide-react";
import CombinedLogo from "@/components/CombinedLogo";

const TRANSASIA_LOGO = "https://customer-assets.emergentagent.com/job_ac147b83-bb9e-4da1-b506-44afecc6b2ce/artifacts/4lmb9h3d_IMG-20260413-WA0117.jpg";
const TAGNPAY_LOGO = "https://customer-assets.emergentagent.com/job_images-34/artifacts/tdji2o49_tagnpay-logo-new.png";

const navItems = [
  { path: "/admin", icon: LayoutDashboard, label: "Dashboard", end: true },
  { path: "/admin/registrations", icon: UserPlus, label: "Registrations" },
  { path: "/admin/users", icon: Users, label: "Users" },
  { path: "/admin/products", icon: Package, label: "Products" },
  { path: "/admin/reagent-mapping", icon: FileUp, label: "Reagent Mapping" },
  { path: "/admin/instruments", icon: Microscope, label: "Instruments" },
  { path: "/admin/qr-codes", icon: QrCode, label: "QR Codes" },
  { path: "/admin/qr-generator", icon: Sparkles, label: "QR Generator" },
  { path: "/admin/rejected-qrs", icon: ShieldAlert, label: "Rejected QRs" },
  { path: "/admin/scan-approvals", icon: ScanLine, label: "Scan Approvals" },
  { path: "/admin/erp-diagnostics", icon: Stethoscope, label: "ERP Diagnostic" },
  { path: "/admin/distributors", icon: MapPin, label: "Distributors" },
  { path: "/admin/banners", icon: Image, label: "Banners" },
  { path: "/admin/assets", icon: Sparkles, label: "Brand Assets" },
  { path: "/admin/splash-screens", icon: Layers, label: "Splash Screens" },
  { path: "/admin/content/product-demos", icon: Video, label: "Product Demos" },
  { path: "/admin/content/lab-info", icon: FlaskConical, label: "Lab Info" },
  { path: "/admin/content/training", icon: BookOpen, label: "Training" },
  { path: "/admin/content/news-events", icon: Newspaper, label: "News & Events" },
  { path: "/admin/enquiries", icon: MessageSquare, label: "Enquiries" },
  { path: "/admin/complaints", icon: Headphones, label: "Complaints" },
  { path: "/admin/redemptions", icon: Gift, label: "Redemptions" },
  { path: "/admin/reports", icon: BarChart3, label: "Reports" },
  { path: "/admin/api-logs", icon: ScrollText, label: "API Logs" },
  { path: "/admin/config", icon: Settings, label: "Config" },
];

const superAdminNavItems = [
  { path: "/admin/admin-users", icon: ShieldCheck, label: "Admin Users" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const handleLogout = async () => { await logout(); navigate("/admin/login"); };
  const items = user?.is_super_admin ? [...navItems, ...superAdminNavItems] : navItems;

  // Live pending scan-approvals count for the sidebar badge (Point 23).
  const [pendingScans, setPendingScans] = useState(0);
  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const { data } = await API.get("/admin/scan-sessions?status=pending&limit=1");
        if (active) setPendingScans(data?.pending_count || 0);
      } catch { /* ignore */ }
    };
    load();
    const id = setInterval(load, 30000);
    return () => { active = false; clearInterval(id); };
  }, []);

  return (
    <div className="min-h-screen bg-[#F4F7FA] flex" data-testid="admin-layout">
      <aside className="hidden md:flex w-[250px] bg-white border-r border-gray-200 flex-col fixed inset-y-0 left-0 z-40">
        <div className="px-5 py-4 border-b border-gray-100">
          <CombinedLogo size="small" />
          <p className="text-[9px] text-gray-400 font-bold uppercase tracking-wider mt-1">Admin Portal</p>
        </div>
        <nav className="flex-1 px-3 py-3 space-y-0.5 overflow-y-auto">
          {items.map((item) => (
            <NavLink key={item.path} to={item.path} end={item.end}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${isActive ? "bg-[#E0F4F5] text-[#008A99]" : "text-gray-500 hover:bg-gray-50 hover:text-gray-800"}`}
              data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
            ><item.icon className="w-[18px] h-[18px]" /><span className="flex-1">{item.label}</span>{item.path === "/admin/scan-approvals" && pendingScans > 0 && (<span className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid="scan-approvals-badge">{pendingScans}</span>)}</NavLink>
          ))}
        </nav>
        <div className="px-3 py-3 border-t border-gray-100">
          <div className="px-3 py-2"><p className="text-xs font-medium text-gray-800 truncate">{user?.full_name || 'Admin'}</p><p className="text-[10px] text-gray-400 truncate">{user?.email}</p></div>
          <button onClick={handleLogout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-gray-500 hover:bg-red-50 hover:text-red-600 transition-all" data-testid="admin-logout-button"><LogOut className="w-[18px] h-[18px]" /> Logout</button>
        </div>
        <div className="px-5 pb-3 flex items-center gap-2 opacity-40"><span className="text-[8px] uppercase tracking-[0.15em] text-gray-500 font-semibold">Powered by</span><img src={TAGNPAY_LOGO} alt="TagNPay" className="h-3" /></div>
      </aside>
      <div className="md:hidden fixed top-0 left-0 right-0 z-50 glass border-b border-gray-200/60 px-4 py-3">
        <div className="flex items-center justify-between"><CombinedLogo size="small" /><button onClick={handleLogout} className="p-2 rounded-lg hover:bg-gray-100 text-gray-400"><LogOut className="w-5 h-5" /></button></div>
        <div className="flex gap-1.5 mt-2 overflow-x-auto pb-1">{items.map((item) => (<NavLink key={item.path} to={item.path} end={item.end} className={({ isActive }) => `flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${isActive ? "bg-[#008A99] text-white" : "bg-gray-100 text-gray-500"}`}><item.icon className="w-3.5 h-3.5" />{item.label}{item.path === "/admin/scan-approvals" && pendingScans > 0 && (<span className="ml-1 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center">{pendingScans}</span>)}</NavLink>))}</div>
      </div>
      <main className="flex-1 md:ml-[250px] px-4 sm:px-6 lg:px-8 py-6 md:py-8 mt-24 md:mt-0"><div className="max-w-6xl mx-auto"><Outlet /></div></main>
    </div>
  );
}
